import type { FeatureFlagService } from "../services/feature-flags";
import {
  type MinimalFetch,
  checkForUpdate,
  describeUpdateCheck,
} from "../services/update-check";
import { decideRequest } from "../services/update-policy";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

export const SERVICE_WORKER_PATH = "/sw.js";

export interface UpdateBandOptions {
  readonly appVersion: string;
  readonly flags: FeatureFlagService;
  readonly onFailure: (failure: unknown) => void;
}

/**
 * Install and update band. R1 Step 15, behind the `appUpdates` flag.
 *
 * Two jobs, both bounded by hard rule 1:
 *
 * 1. Register the service worker, so the app installs and runs offline after
 *    first load. The worker caches the app's own assets and nothing else.
 * 2. Check whether a newer build is published, by reading `/version.json` and
 *    no other address. The request is put through `decideRequest` first.
 *
 * Neither path can carry user content: the registration is a fixed same-origin
 * path with no query string, and the version check is refused before `fetch`
 * if anything about it fails the policy.
 */
export function mountUpdateBand(
  target: HTMLElement,
  options: UpdateBandOptions,
): void {
  if (!options.flags.isEnabled("appUpdates")) return;

  const band = element("section");
  band.className = "update-band";
  band.dataset.role = "update-band";
  band.append(element("h2", "Install and updates"));

  const installed = element(
    "p",
    `Running build ${options.appVersion}. Once loaded, ATLas works with no network at all.`,
  );
  const status = element("p", "Checking for a newer build…");
  status.dataset.role = "update-status";

  band.append(installed, status);
  target.append(band);

  void registerWorker(status, options.onFailure);
  void runCheck(status, options);
}

async function registerWorker(
  status: HTMLElement,
  onFailure: (failure: unknown) => void,
): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    // No offline install here. The app still runs; it just reloads from the
    // network every time. Say so rather than failing silently.
    status.dataset.install = "unsupported";
    return;
  }
  // Hard rule 1, asserted rather than assumed: the worker's own address goes
  // through the same policy as every other request the app makes.
  const decision = decideRequest(
    {
      method: "GET",
      url: new URL(SERVICE_WORKER_PATH, window.location.origin).href,
      body: null,
    },
    window.location.origin,
  );
  /* v8 ignore next 4 -- a fixed same-origin path; unreachable unless edited */
  if (decision.verdict === "blocked") {
    status.dataset.install = "refused";
    return;
  }
  try {
    await navigator.serviceWorker.register(SERVICE_WORKER_PATH, {
      scope: "/",
    });
    status.dataset.install = "installed";
  } catch (failure: unknown) {
    status.dataset.install = "failed";
    onFailure(failure);
  }
}

async function runCheck(
  status: HTMLElement,
  options: UpdateBandOptions,
): Promise<void> {
  const check = await checkForUpdate({
    fetch: window.fetch.bind(window) as MinimalFetch,
    installedVersion: options.appVersion,
    origin: window.location.origin,
  });
  status.textContent = describeUpdateCheck(check);
  status.dataset.status = check.status;
}
